import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OutlineButtonComponent } from '../../../../shared/components/outline-button/outline-button';
import { PrimaryButtonComponent } from '../../../../shared/components/primary-button/primary-button';
import {
  ExamResultApiData,
  ExamResultApiSection,
  ExamResultState,
  ExamSectionSummary,
} from '../../model/test-series.model';
import { TestSeriesService } from '../../services/test-series.service';
import { formatDisplayDate } from '../../../../shared/utils/date.util';

interface SummaryMetric {
  id: string;
  label: string;
  value: string;
  tone?: 'default' | 'green' | 'red' | 'amber' | 'blue';
}

@Component({
  selector: 'app-exam-summary',
  imports: [OutlineButtonComponent, PrimaryButtonComponent],
  templateUrl: './exam-summary.html',
  styleUrl: './exam-summary.css',
})
export class ExamSummaryComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly testSeriesService = inject(TestSeriesService);

  private readonly BADGE_TONES: ExamSectionSummary['badgeTone'][] = [
    'indigo',
    'green',
    'orange',
    'violet',
    'teal',
  ];

  protected readonly isLoading = signal(true);
  protected readonly hasError = signal(false);
  protected readonly canGoBack = signal(false);
  protected readonly accuracyTip = signal<{ top: number; right: number } | null>(null);
  protected readonly accuracyTipVisible = signal(false);

  private origin: 'dashboard' | 'test-series' | 'performance' = 'test-series';

  protected examResultState: ExamResultState = {
    examTitle: 'National NEET-PG Mock 2024 (New NEET-PG Pattern)',
    examMeta: 'Full Mock Test · Jul 16, 2026',
    submittedAt: new Date().toISOString(),
    candidateName: '',
    candidateInitials: '',
    performanceLabel: '',
    submissionSummary: {
      totalQuestions: 0,
      totalAttempted: 0,
      totalUnattempted: 0,
      markedForReview: 0,
      timeTaken: '-',
      remainingTime: '-',
    },
    sectionSummaries: [],
  };

  ngOnInit(): void {
    const fromParam = this.route.snapshot.queryParamMap.get('from');
    if (fromParam === 'dashboard' || fromParam === 'test-series' || fromParam === 'performance') {
      this.origin = fromParam;
      this.canGoBack.set(true);
    }

    this.exitFullscreenIfActive();
    this.loadExamResult();
  }

  protected get metrics(): SummaryMetric[] {
    return [
      {
        id: 'total-questions',
        label: 'Total Questions',
        value: `${this.examResultState.submissionSummary.totalQuestions}`,
        tone: 'default',
      },
      {
        id: 'attempted',
        label: 'Attempted',
        value: `${this.examResultState.submissionSummary.totalAttempted}`,
        tone: 'blue',
      },
      {
        id: 'correct',
        label: 'Correct',
        value: `${this.examResultState.totalCorrect ?? 0}`,
        tone: 'green',
      },
      {
        id: 'wrong',
        label: 'Wrong',
        value: `${this.examResultState.totalWrong ?? 0}`,
        tone: 'red',
      },
      {
        id: 'skipped',
        label: 'Skipped',
        value: `${this.examResultState.totalSkipped ?? 0}`,
        tone: 'amber',
      },
      {
        id: 'accuracy',
        label: 'Accuracy',
        value: `${this.examResultState.totalAccuracy ?? 0}%`,
        tone: 'default',
      },
      {
        id: 'guess',
        label: 'Guess',
        value: `${this.examResultState.totalGuess ?? 0}`,
        tone: 'default',
      },
      {
        id: 'marked-for-review',
        label: 'Marked for Review',
        value: `${this.examResultState.totalMarkedForReview ?? 0}`,
        tone: 'default',
      },
      {
        id: 'correct-guess',
        label: 'Correct Guess',
        value: `${this.examResultState.totalCorrectGuess ?? 0}`,
        tone: 'default',
      },
      {
        id: 'not-visited',
        label: 'Not Visited',
        value: `${this.examResultState.totalNotVisited ?? 0}`,
        tone: 'default',
      },
    ];
  }

  protected get scoreText(): string {
    const display =
      this.examResultState.totalScoreDisplay ??
      `${this.examResultState.totalMarksObtained ?? 0}/${this.examResultState.maximumMarks ?? 0}`;

    return this.formatFractionDisplay(display);
  }

  protected get sections(): ExamSectionSummary[] {
    return this.examResultState.sectionSummaries;
  }

  protected get performanceChipClass(): string {
    const label = this.examResultState.performanceLabel?.toLowerCase() ?? '';

    if (label.includes('excellent') || label.includes('great')) {
      return 'summary-hero-chip summary-hero-chip-green';
    }

    if (label.includes('good') || label.includes('progress')) {
      return 'summary-hero-chip summary-hero-chip-blue';
    }

    if (label.includes('needs improvement') || label.includes('keep practicing')) {
      return 'summary-hero-chip summary-hero-chip-orange';
    }

    return 'summary-hero-chip summary-hero-chip-green';
  }

  protected getMetricClass(tone?: string): string {
    const baseClass = 'rounded-lg border text-[14px] font-medium text-white w-fit h-fit';

    if (tone === 'blue') {
      return `${baseClass} bg-[#2563EB33] border-[#2563EB]`;
    }

    if (tone === 'green') {
      return `${baseClass} bg-[#26AB5B33] border-[#26AB5B]`;
    }

    if (tone === 'red') {
      return `${baseClass} bg-[#E74C3C33] border-[#E74C3C]`;
    }

    if (tone === 'amber') {
      return `${baseClass} bg-[#D9770633] border-[#D97706]`;
    }

    return `${baseClass} bg-white/10 border-white/20`;
  }

  protected showAccuracyTip(event: Event): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    // Right-align the tooltip so its arrow (16px from its right edge) points at the icon's centre.
    this.accuracyTip.set({
      top: rect.bottom + 8,
      right: window.innerWidth - (rect.left + rect.width / 2) - 16,
    });
    this.accuracyTipVisible.set(true);
  }

  protected getProgressWidth(accuracy: number): string {
    return `${Math.max(0, Math.min(accuracy, 100))}%`;
  }

  protected onStartNewTest(): void {
    const testId = this.route.snapshot.paramMap.get('id');

    if (!testId) {
      void this.router.navigate(['/test-series']);
      return;
    }

    void this.router.navigate(['/test-series', testId, 'exam-loading']);
  }

  protected onBackToDashboard(): void {
    void this.router.navigate(['/dashboard']);
  }

  protected onReview(): void {
    const testId = this.route.snapshot.paramMap.get('id');

    if (!testId) {
      void this.router.navigate(['/test-series']);
      return;
    }

    void this.router.navigate(['/test-series', testId, 'review'], {
      queryParams: { from: this.origin },
    });
  }

  protected getBadgeClass(tone: ExamSectionSummary['badgeTone']): string {
    return `section-badge section-badge-${tone}`;
  }

  private async exitFullscreenIfActive(): Promise<void> {
    if (!document.fullscreenElement || !document.exitFullscreen) {
      return;
    }

    try {
      await document.exitFullscreen();
    } catch {
      // Keep the summary accessible even if the browser blocks fullscreen exit here.
    }
  }

  private loadExamResult(): void {
    const examId = this.route.snapshot.paramMap.get('id');

    if (!examId) {
      this.isLoading.set(false);
      this.hasError.set(true);
      return;
    }

    this.testSeriesService.getExamResult(examId).subscribe({
      next: (response) => {
        if (response?.code === '200' && response.data) {
          this.examResultState = this.mapApiResponseToState(response.data);
        } else {
          this.hasError.set(true);
        }

        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.hasError.set(true);
      },
    });
  }

  private mapApiResponseToState(data: ExamResultApiData): ExamResultState {
    const submittedDate = data.submitted_at ? new Date(data.submitted_at) : null;

    const examMeta = submittedDate
      ? `${data.subject ?? 'All Subjects'} · ${formatDisplayDate(submittedDate)}`
      : (data.subject ?? 'All Subjects');

    const sectionSummaries: ExamSectionSummary[] = (data.sections ?? []).map((section, index) =>
      this.mapApiSectionToSummary(section, index),
    );

    return {
      examTitle: data.exam_name ?? 'Exam Result',
      examMeta,
      submittedAt: data.submitted_at,
      candidateName: '',
      candidateInitials: '',
      performanceLabel: data.performance_label ?? '',
      totalScoreDisplay: data.total_score_display,
      maximumMarks: data.maximum_marks,
      cutoffMarks: data.cutoff_marks,
      totalMarksObtained: data.total_marks_obtained,
      totalCorrect: data.total_correct,
      totalWrong: data.total_wrong,
      totalSkipped: data.total_skipped,
      totalAccuracy: data.total_accuracy,
      totalGuess: data.total_guess,
      totalCorrectGuess: data.total_correct_guess,
      totalMarkedForReview: data.total_marked_for_review,
      totalNotVisited: data.total_not_visited,
      submissionSummary: {
        totalQuestions: data.total_questions,
        totalAttempted: data.total_attempted,
        totalUnattempted: data.total_questions - data.total_attempted,
        markedForReview: data.total_marked_for_review ?? 0,
        timeTaken: '-',
        remainingTime: '-',
      },
      sectionSummaries,
    };
  }

  private formatFractionDisplay(display: string): string {
    const [numerator, denominator] = display.split('/');
    if (numerator === undefined || denominator === undefined) {
      return display;
    }

    const numeratorValue = parseFloat(numerator);
    const formattedNumerator = Number.isInteger(numeratorValue)
      ? String(numeratorValue)
      : this.formatDecimal(numerator);

    return `${formattedNumerator} / ${this.formatDecimal(denominator)}`;
  }

  private formatDecimal(value: string): string {
    const trimmed = value.trim();
    const numericValue = parseFloat(trimmed);
    if (Number.isNaN(numericValue)) {
      return trimmed;
    }
    if (Number.isInteger(numericValue)) {
      return String(numericValue);
    }

    // Round to at most 2 decimal places, dropping trailing zeros (e.g. 2.666 -> 2.67, 2.5 -> 2.5).
    return String(Math.round((numericValue + Number.EPSILON) * 100) / 100);
  }

  private getSectionInitials(name: string): string {
    const words = (name ?? '')
      .replace(/^Group\s+/i, '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (words.length === 1 && words[0].length <= 2) {
      return words[0].toUpperCase();
    }

    // Keep numbers whole (e.g. "Part 1" -> "P1"), otherwise take each word's first letter.
    return words
      .map((word) => (/^\d+$/.test(word) ? word : word.charAt(0)))
      .join('')
      .slice(0, 3)
      .toUpperCase();
  }

  private mapApiSectionToSummary(section: ExamResultApiSection, index: number): ExamSectionSummary {
    const badge = this.getSectionInitials(section.section_name);

    return {
      id: `section-${index}`,
      label: section.section_name,
      badge: badge || String.fromCharCode(65 + index),
      badgeTone: this.BADGE_TONES[index % this.BADGE_TONES.length],
      totalQuestions: section.total_questions,
      answered: section.answered,
      unanswered: section.unanswered,
      markedForReview: section.marked_for_review,
      marksObtained: this.formatFractionDisplay(section.marks_obtained_display),
      accuracy: section.accuracy,
      guessedAnswers: '',
      correct: section.correct,
      wrong: section.wrong,
      guess: section.guess,
      correctGuess: section.correct_guess,
      notVisited: section.not_visited,
      skipped: section.skipped,
    };
  }
}
